/* eslint-disable max-classes-per-file */
import React, { Component } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Image, PixelRatio, StyleSheet, Platform, Animated } from 'react-native'
import { bindingTypes } from '@adalo/constants'

import { addImageLoaded, isImageLoaded } from 'ducks/images'
import { getScaledSources } from '../utils/images'
import { solveBindings } from '../utils/library-binding'
import { applyImgixParameters, isObject } from '../utils/imgix'
import Section from './Section'
import ImageBlurhash from './ImageBlurhash'

const libraryComponentManifest = {
  props: [
    {
      name: 'image',
      displayName: 'Image',
      type: 'image',
    },
  ],
}

class ImageView extends Section {
  static contextTypes = {
    getAssetURL: PropTypes.func,
    getImageUploadsBaseURL: PropTypes.func,
  }

  constructor(props) {
    super(props)
    this.state = {
      layout: null,
      blurhashLayout: null,
      blurOpacity: new Animated.Value(1),
      onloadCalled: false,
    }
  }

  getURL = () => {
    const { object, componentProps, getAssetURL } = this.props

    if (object.attributes.imageType === 'dynamic') {
      if (componentProps && componentProps.image) {
        const image = Array.isArray(componentProps.image)
          ? componentProps.image[0]
          : componentProps.image
        return image.uri
      } else if (
        object &&
        object.attributes &&
        object.attributes.imageBinding &&
        typeof object.attributes.imageBinding.options !== 'undefined'
      ) {
        const { options } = object.attributes.imageBinding
        const placeholderImageEnabled =
          typeof options.placeholderImageEnabled !== 'undefined'
            ? options.placeholderImageEnabled
            : null
        const placeholderImage =
          typeof options.placeholderImage !== 'undefined'
            ? options.placeholderImage
            : null
        if (placeholderImageEnabled && placeholderImage) {
          return getAssetURL(options.placeholderImage)
        }
      } else {
        return undefined
      }
    }

    if (object.attributes.imageType === 'url') {
      const { attributes } = object
      if (Array.isArray(attributes.imageURL)) {
        if (componentProps && componentProps.image) {
          return componentProps.image.uri
        } else if (
          attributes &&
          attributes.imageBinding &&
          typeof attributes.imageBinding.options !== 'undefined'
        ) {
          const { options } = object.attributes.imageBinding
          const placeholderImageEnabled =
            typeof options.placeholderImageEnabled !== 'undefined'
              ? options.placeholderImageEnabled
              : null
          const placeholderImage =
            typeof options.placeholderImage !== 'undefined'
              ? options.placeholderImage
              : null
          if (placeholderImageEnabled && placeholderImage) {
            return getAssetURL(options.placeholderImage)
          } else {
            return undefined
          }
        } else {
          return undefined
        }
      }

      return object.attributes.imageURL
    }

    const bindingInfo = this.getDataBindingOfType(bindingTypes.SET_IMAGE)
    if (bindingInfo) {
      let { binding, data } = bindingInfo
      if (data && typeof data === 'object') {
        data = data.url
      }

      const baseURL = this.context.getImageUploadsBaseURL()

      if (data || !binding.includeDefault) {
        if (data && typeof data === 'string' && data.match(/^(file|data):/)) {
          return data
        }

        return data && `${baseURL}/${data}?orient`
      }
    }

    const ratio = PixelRatio.getPixelSizeForLayoutSize(1)
    let sizes = [3, 2, 1]
    sizes.sort((a, b) => Math.abs(ratio - a) - Math.abs(ratio - b))

    const largerSizes = sizes.filter(s => s >= ratio)
    const smallerSizes = sizes.filter(s => s < ratio)
    sizes = largerSizes.concat(smallerSizes)

    let filename = null

    if (object.imageId) {
      filename = `${object.imageId}@${sizes[0]}x.png`
    } else {
      for (let i = 0; i < sizes.length; i += 1) {
        const size = sizes[i]
        const key = `filename${size}x`

        if (object.attributes[key]) {
          filename = object.attributes[key]
          break
        }
      }
    }

    if (!filename) {
      return null
    }

    if (typeof filename === 'object') {
      filename = filename.url
    }

    return this.context.getAssetURL(filename)
  }

  getSource = () => {
    const { bindingData, object } = this.props
    const { attributes } = object

    let src = this.getURL()
    if (typeof src === 'string' && src.match('^http://')) {
      src = src.replace(/^http:\/\//i, 'https://')
    }

    const cache = { cache: 'force-cache' }
    const isWeb = Platform.OS === 'web'

    if (src && bindingData && typeof bindingData === 'object' && !isWeb) {
      return getScaledSources(src, bindingData.width, bindingData.height)
    }

    // URL images
    if (attributes.imageType === 'url') {
      const { imageURL } = attributes
      if (!Array.isArray(imageURL)) return { uri: imageURL }
    }

    // Local images
    if (typeof src === 'number') return src

    // Remote images
    return { ...cache, uri: src }
  }

  /**
   *
   * @param {string} uri
   */
  onImageLoad = uri => {
    const { hasImageBlurhash, addImageLoaded, hasImageLoaded } = this.props
    const { blurOpacity } = this.state

    if (hasImageBlurhash) {
      // if the image uri has already been loaded
      // we don't want to animate (or display, see render method) the blurhash
      if (hasImageLoaded(uri)) {
        blurOpacity.setValue(0)
      } else {
        Animated.timing(blurOpacity, {
          toValue: 0,
          duration: 250,
          delay: 0,
          useNativeDriver: Platform.OS !== 'web',
        }).start(({ finished }) => {
          // if the animation finished, we want to add the image to the list of loaded images
          // this is to prevent the blurhash from being displayed again
          if (finished) {
            addImageLoaded(uri)
          }
        })
      }
    }
  }

  onImageLayout = e => {
    if (!this.state.layout) {
      this.setState({ layout: e.nativeEvent.layout })
    }

    const { hasImageBlurhash } = this.props

    if (hasImageBlurhash) {
      this.setState({
        blurhashLayout: e.nativeEvent.layout,
      })
    }
  }

  renderBackground() {
    const { object, componentProps, hasImageBlurhash } = this.props
    const { blurOpacity, blurhashLayout } = this.state

    const { attributes } = object

    const source = this.getSource()
    const formattedSource = applyImgixParameters(source, this.state.layout)
    const imageUri = isObject(formattedSource)
      ? formattedSource.uri
      : formattedSource

    const imageComponent = (
      <Image
        style={styles.image}
        resizeMode={attributes.imageResize || 'cover'}
        source={formattedSource}
        onLayout={this.onImageLayout}
        onLoad={() => {
          if (this.state.onloadCalled) {
            return
          }

          this.setState({ onloadCalled: true }, () => {
            this.onImageLoad(imageUri)
          })
        }}
      />
    )

    if (hasImageBlurhash) {
      return (
        <>
          <Animated.View
            style={[styles.imageBlurhash, { opacity: blurOpacity }]}
          >
            <ImageBlurhash
              object={object}
              source={source}
              componentProps={componentProps}
              layout={blurhashLayout}
            />
          </Animated.View>
          {imageComponent}
        </>
      )
    }

    return imageComponent
  }
}

const styles = StyleSheet.create({
  image: {
    flex: 1,
    width: null,
    height: null,
    zIndex: 0,
  },
  imageBlurhash: {
    position: 'absolute',
    zIndex: 1,
    width: '100%',
    height: '100%',
  },
})

const mapStateToProps = (state, props) => {
  const {
    object,
    getApp,
    getBinding,
    getParams,
    getImageUploadsBaseURL,
    listItemId,
  } = props

  const hasImageLoaded = uri => isImageLoaded(state, uri)

  if (
    object.attributes.imageType === 'dynamic' ||
    object.attributes.imageType === 'url'
  ) {
    const app = getApp && getApp()

    const { width, height } = object.attributes

    const image =
      object.attributes.imageType === 'url'
        ? { type: 'binding', source: object.attributes.imageURL }
        : object.attributes.imageBinding

    const fakeObject = {
      attributes: {
        image,
        width,
        height,
        listItemId,
      },
      libraryComponentManifest,
    }

    return {
      ...solveBindings(app, fakeObject, {
        state,
        getBinding,
        getParams,
        getImageUploadsBaseURL,
      }),
      hasImageLoaded,
    }
  }

  return {
    hasImageLoaded,
  }
}

const mapDispatchToProps = dispatch => {
  return {
    addImageLoaded: url => dispatch(addImageLoaded(url)),
  }
}

const ConnectedImageView = connect(
  mapStateToProps,
  mapDispatchToProps
)(ImageView)

export default class WrappedImageView extends Component {
  static contextTypes = {
    getApp: PropTypes.func,
    getBinding: PropTypes.func,
    getParams: PropTypes.func,
    getImageUploadsBaseURL: PropTypes.func,
    getAssetURL: PropTypes.func,
    getFlags: PropTypes.func,
  }

  render() {
    const {
      getApp,
      getBinding,
      getParams,
      getImageUploadsBaseURL,
      getAssetURL,
      getFlags,
    } = this.context

    const { hasImageBlurhash } = getFlags()

    return (
      <ConnectedImageView
        {...this.props}
        getApp={getApp}
        getBinding={getBinding}
        getParams={getParams}
        getImageUploadsBaseURL={getImageUploadsBaseURL}
        getAssetURL={getAssetURL}
        hasImageBlurhash={hasImageBlurhash}
      />
    )
  }
}
